import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AuthBrandPanel, { AuthBrandMark } from '../components/auth/AuthBrandPanel';
import { Button, Input } from '@copilot/ui';
import { Field } from '../components/common/Field';
import { useAuth } from '../hooks/useAuth';
import { getAuthenticatedHomePath } from '../utils/authRedirects';
import { getUserFriendlyError } from '../utils/errors';

// Branch on the machine-readable code, never the human text - that copy
// is server-side and may be reworded.
const ATTEMPTS_EXCEEDED = 'VERIFICATION_ATTEMPTS_EXCEEDED';
const RESEND_COOLDOWN = 'VERIFICATION_RESEND_COOLDOWN';
const RESEND_LIMIT = 'VERIFICATION_RESEND_LIMIT';
const ACCOUNT_DISABLED = 'ACCOUNT_DISABLED';

// Mirrors the backend's EMAIL_OTP_RESEND_COOLDOWN_SECONDS. Only a
// courtesy so the button does not invite a request the server will
// refuse - the server enforces the real limit regardless of what this
// page does.
const RESEND_COOLDOWN_SECONDS = 60;

function VerifyEmailPage() {
  const { verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Carried in navigation state by whoever sent the user here (register,
  // or a login that hit EMAIL_NOT_VERIFIED). Falls back to the query
  // string so the page survives a refresh.
  const params = new URLSearchParams(location.search);
  const email = location.state?.email || params.get('email') || '';

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  // Nothing to verify without an address. Sending them back to register
  // is better than showing a form whose every submission must fail.
  if (!email) {
    return (
      <div className="auth-page">
        <div className="auth-shell">
          <AuthBrandPanel />
          <div className="auth-card">
            <AuthBrandMark className="mobile-only" />
            <section className="auth-form-panel">
              <div className="auth-heading">Check your email</div>
              <p className="auth-panel-subtitle">
                We need to know which address to verify.
              </p>
              <Link to="/register">Back to sign up</Link>
            </section>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setIsSubmitting(true);

    try {
      const me = await verifyEmail({ email, code: code.trim() });
      navigate(getAuthenticatedHomePath(me), { replace: true });
    } catch (err) {
      if (err.code === ATTEMPTS_EXCEEDED) {
        setError(
          'Too many incorrect attempts. Request a new code to try again.'
        );
        setSecondsLeft(0);
      } else if (err.code === ACCOUNT_DISABLED) {
        setError('Your account has been suspended. Please contact administrator.');
      } else {
        setError(getUserFriendlyError(err, 'Unable to connect. Please try again.'));
      }
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setNotice('');

    try {
      await resendVerification({ email });
      setNotice('A new code is on its way. The previous one no longer works.');
      setSecondsLeft(RESEND_COOLDOWN_SECONDS);
      setCode('');
    } catch (err) {
      if (err.code === RESEND_COOLDOWN) {
        setError('A code was just sent. Please wait a moment before asking for another.');
      } else if (err.code === RESEND_LIMIT) {
        setError(
          'Too many codes have been requested for this address. Please try again later.'
        );
      } else {
        setError(getUserFriendlyError(err, 'Unable to connect. Please try again.'));
      }
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <AuthBrandPanel />

        <div className="auth-card">
          <AuthBrandMark className="mobile-only" />

          <section className="auth-form-panel" aria-labelledby="verify-heading">
            <div className="auth-heading" id="verify-heading">
              Verify your email
            </div>
            <p className="auth-panel-subtitle">
              We sent a code to <strong>{email}</strong>. Enter it below to
              finish setting up your account.
            </p>

            <form className="auth-form" onSubmit={handleSubmit}>
              <Field label="Verification code" htmlFor="verify-code">
                <Input
                  id="verify-code"
                  autoFocus
                  // inputMode/autoComplete let phones show a number pad
                  // and offer the code straight from the mail app.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8))
                  }
                  required
                />
              </Field>

              {error && <div className="form-alert">{error}</div>}
              {notice && (
                <div className="form-alert" role="status">
                  {notice}
                </div>
              )}

              <Button type="submit" block disabled={isSubmitting || !code}>
                {isSubmitting ? 'Verifying...' : 'Verify and continue'}
              </Button>
            </form>

            <div className="auth-row-between" style={{ marginTop: 'var(--space-4)' }}>
              <span>Didn&apos;t get it?</span>
              <Button
                type="button"
                variant="outline"
                onClick={handleResend}
                disabled={secondsLeft > 0}
              >
                {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code'}
              </Button>
            </div>
          </section>

          <div className="auth-footer-link">
            Wrong address? <Link to="/register">Start over</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
